<div class="scheduling-page">
  <div class="scheduling-page__container">
    @if (!loading() && !loadError() && sections().length > 0) {
      <div class="scheduling-page__rail">
        <button
          type="button"
          class="scheduling-page__search-toggle"
          [attr.aria-label]="(searchOpen() ? 'SCHEDULING.CLOSE_SEARCH' : 'SCHEDULING.OPEN_SEARCH') | translate"
          [attr.aria-expanded]="searchOpen()"
          (click)="toggleSearch()"
        >
          <mat-icon>{{ searchOpen() ? 'close' : 'search' }}</mat-icon>
        </button>

        @if (searchOpen()) {
          <div class="scheduling-page__search-field" [class.scheduling-page__search-field--closing]="searchClosing()">
            <mat-icon>search</mat-icon>
            <input
              #searchInput
              type="text"
              [placeholder]="'SCHEDULING.SEARCH_SERVICES_PLACEHOLDER' | translate"
              [ngModel]="searchQuery()"
              (ngModelChange)="searchQuery.set($event)"
              (keydown.escape)="closeSearch()"
            />
          </div>
        }

        @if (sections().length > 1) {
          <div class="scheduling-page__chips">
            @for (section of sections(); track section.id) {
              <button
                type="button"
                class="scheduling-page__chip"
                [class.scheduling-page__chip--active]="activeSectionId() === section.id"
                (click)="goToSection(section.id)"
              >
                {{ section.name }}
              </button>
            }
          </div>
          <div class="scheduling-page__section-menu">
            <button
              type="button"
              class="scheduling-page__section-menu-trigger"
              [attr.aria-expanded]="sectionMenuOpen()"
              aria-haspopup="listbox"
              (click)="sectionMenuOpen.set(!sectionMenuOpen())"
            >
              <span>{{ activeSectionName() }}</span>
              <mat-icon>{{ sectionMenuOpen() ? 'expand_less' : 'expand_more' }}</mat-icon>
            </button>
            @if (sectionMenuOpen()) {
              <div class="scheduling-page__section-menu-backdrop" (click)="sectionMenuOpen.set(false)"></div>
              <div class="scheduling-page__section-menu-list" role="listbox">
                @for (section of sections(); track section.id) {
                  <button
                    type="button"
                    role="option"
                    class="scheduling-page__section-menu-item"
                    [class.scheduling-page__section-menu-item--active]="activeSectionId() === section.id"
                    [attr.aria-selected]="activeSectionId() === section.id"
                    (click)="goToSection(section.id)"
                  >
                    {{ section.name }}
                  </button>
                }
              </div>
            }
          </div>
        }
      </div>
    }

    <div class="scheduling-page__content">
      <div class="scheduling-page__main">
        @if (loading()) {
          <div class="scheduling-page__section">
            <div class="scheduling-page__section-header">
              <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--title"></div>
            </div>
            @for (i of [1, 2, 3]; track i) {
              <div class="scheduling-page__row scheduling-page__row--skeleton">
                <div class="scheduling-page__row-thumb scheduling-page__skeleton-block"></div>
                <div class="scheduling-page__skeleton-lines">
                  <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--long"></div>
                  <div class="scheduling-page__skeleton-line scheduling-page__skeleton-line--short"></div>
                </div>
              </div>
            }
          </div>
        } @else if (loadError()) {
          <app-booking-error
            title="Unable to load services"
            message="Something went wrong while loading the services. Please try again later."
          ></app-booking-error>
        } @else if (sections().length === 0) {
          <div class="scheduling-page__error">
            <p>{{ 'SCHEDULING.NO_SERVICES_AVAILABLE' | translate }}</p>
          </div>
        } @else if (visibleSections().length === 0) {
          <p class="scheduling-page__no-results">
            {{ 'SCHEDULING.NO_SERVICES_FOUND_MATCHING' | translate: { searchQuery: searchQuery() } }}
          </p>
        } @else {
          @for (section of visibleSections(); track section.id) {
            <section class="scheduling-page__section" [id]="'section-' + section.id">
              <div class="scheduling-page__section-header">
                <div class="scheduling-page__section-heading">
                  @if (section.name) {
                    <h2 class="scheduling-page__section-title">{{ section.name }}</h2>
                  }
                  @if (section.description) {
                    <p class="scheduling-page__section-description">{{ section.description }}</p>
                  }
                </div>
                <div class="scheduling-page__section-meta">
                  <span>
                    {{
                      (section.events.length === 1
                        ? 'BOOKING_CART.SERVICE_COUNT_ONE'
                        : 'BOOKING_CART.SERVICE_COUNT_MANY'
                      ) | translate: { count: section.events.length }
                    }}
                  </span>
                  @if (section.single) {
                    <span class="scheduling-page__section-hint">{{ 'SCHEDULING.SELECT_ONE' | translate }}</span>
                  }
                </div>
              </div>

              @for (event of section.events; track event.id) {
                <div
                  class="scheduling-page__row"
                  [class.scheduling-page__row--selected]="isEventSelected(event)"
                  [class.scheduling-page__row--disabled]="isRowDisabled(event, section)"
                  [class.scheduling-page__row--unavailable]="isEventUnavailable(event)"
                  (click)="onRowToggle(event, section)"
                >
                  <div class="scheduling-page__row-thumb">{{ getInitials(event.name) }}</div>

                  <div class="scheduling-page__row-info">
                    <div class="scheduling-page__row-heading">
                      <span class="scheduling-page__row-name">{{ event.name }}</span>
                      <span class="scheduling-page__row-duration">
                        <mat-icon>schedule</mat-icon>
                        {{ durationMinutes(event.duration) }}
                      </span>
                    </div>
                    @if (event.description) {
                      <span class="scheduling-page__row-description" [title]="event.description">{{
                        event.description
                      }}</span>
                    }
                  </div>

                  @if (isEventUnavailable(event)) {
                    <glxy-badge class="scheduling-page__row-unavailable" color="yellow" size="small">{{
                      'SCHEDULING.CURRENTLY_UNAVAILABLE' | translate
                    }}</glxy-badge>
                  }
                  <button
                    type="button"
                    class="scheduling-page__row-toggle"
                    [class.scheduling-page__row-toggle--radio]="section.single"
                    [class.scheduling-page__row-toggle--active]="isEventSelected(event)"
                    [attr.role]="section.single ? 'radio' : 'checkbox'"
                    [attr.aria-checked]="isEventSelected(event)"
                    [attr.aria-label]="event.name"
                    [disabled]="isRowDisabled(event, section) || isEventUnavailable(event)"
                    (click)="$event.stopPropagation(); onRowToggle(event, section)"
                  >
                    @if (!section.single) {
                      <mat-icon>{{ isEventSelected(event) ? 'check' : 'add' }}</mat-icon>
                    }
                  </button>
                </div>
              }
            </section>
          }
        }
      </div>

      <div class="scheduling-page__cart-wrapper">
        <app-booking-cart
          [items]="cartItems()"
          [ctaLabel]="cartCtaLabel() | translate"
          [ctaDisabled]="selectedEvents().length === 0"
          [warningMessage]="isMaxEventsReached() ? ('SCHEDULING.MAX_SERVICES_WARNING' | translate) : ''"
          (ctaClick)="onChooseProvider()"
          (removeItem)="onRemoveCartItem($event)"
        ></app-booking-cart>
      </div>
    </div>
  </div>
</div>
